<ng-container *transloco="let t; read: 'expenses'">
  <form
    *ngIf="mode === 'own'"
    [formGroup]="filterForm"
    (ngSubmit)="onSubmitFilter()"
    class="flex flexDirRow justSpaceBetween twoCol"
  >
    <div class="flexItem mbs">
      <label for="statusFilter">{{ t('filterStatus') }}</label>
      <kendo-dropdownlist
        formControlName="status"
        id="statusFilter"
        [data]="statusFilterItems"
        [defaultItem]="allStatusesItem"
        [valuePrimitive]="false"
      >
        <ng-template kendoDropDownListItemTemplate let-dataItem>
          <ng-container *ngIf="dataItem.value == null">{{ t('filterAllStatuses') }}</ng-container>
          <ng-container *ngIf="dataItem.value != null">
            {{ t('expenseStates.' + statusName(dataItem.value)) }}
          </ng-container>
        </ng-template>
        <ng-template kendoDropDownListValueTemplate let-dataItem>
          <ng-container *ngIf="dataItem.value == null">{{ t('filterAllStatuses') }}</ng-container>
          <ng-container *ngIf="dataItem.value != null">
            {{ t('expenseStates.' + statusName(dataItem.value)) }}
          </ng-container>
        </ng-template>
      </kendo-dropdownlist>
    </div>

    <div class="flexItem mbs">
      <kendo-daterange>
        <kendo-floatinglabel [text]="t('filterDateFrom')" class="mrs">
          <kendo-dateinput
            formControlName="dateRangeStart"
            kendoDateRangeStartInput
          ></kendo-dateinput>
        </kendo-floatinglabel>
        <kendo-floatinglabel [text]="t('filterDateTo')">
          <kendo-dateinput
            formControlName="dateRangeEnd"
            kendoDateRangeEndInput
          ></kendo-dateinput>
        </kendo-floatinglabel>
        <kendo-daterange-popup [animate]="false"></kendo-daterange-popup>
      </kendo-daterange>
    </div>

    <div class="flexItem">
      <button
        type="submit"
        kendoButton
        themeColor="primary"
        class="mbs mrs"
        loadingIndication="/Expenses/SearchMyExpenses"
      >
        {{ t('filterSearch') }}
      </button>
      <button
        type="button"
        kendoButton
        themeColor="secondary"
        (click)="onResetFilter()"
        class="mbs"
        loadingIndication="/Expenses/SearchMyExpenses"
      >
        {{ t('filterReset') }}
      </button>
    </div>
  </form>

  <!-- Row actions, shared by the desktop action column and the mobile card. -->
  <ng-template #rowActions let-dataItem>
    <button kendoButton class="mbs mrs" (click)="open(dataItem)">{{ t('view') }}</button>

    <!-- own / submitted tab -->
    <ng-container *ngIf="mode === 'own' && dataItem.status === ExpenseStatus.Open">
      <button kendoButton themeColor="success" class="mbs mrs" (click)="submitRow(dataItem)">{{ t('submit') }}</button>
      <button kendoButton themeColor="error" class="mbs mrs" (click)="deleteRow(dataItem)">{{ t('delete') }}</button>
    </ng-container>

    <!-- own submitted expense, not yet decided: allow pulling it back to Open -->
    <ng-container *ngIf="mode === 'own' && dataItem.status === ExpenseStatus.Submitted">
      <button kendoButton themeColor="warning" class="mbs mrs" (click)="withdrawRow(dataItem)">{{ t('withdraw') }}</button>
    </ng-container>

    <!-- supervisor approval tab (never decide on your own, unless you may self-approve) -->
    <ng-container *ngIf="mode === 'approval' && canDecide(dataItem)">
      <button kendoButton themeColor="success" class="mbs mrs" (click)="approveRow(dataItem)">{{ t('approve') }}</button>
      <button kendoButton themeColor="error" class="mbs mrs" (click)="openReject(dataItem)">{{ t('reject') }}</button>
    </ng-container>
  </ng-template>

  <kendo-grid
    [data]="expenses"
    [skip]="gridState.skip"
    [pageSize]="gridState.pageSize"
    [pageable]="true"
    (pageChange)="onPageChange($event)"
    scrollable="none"
    [selectable]="mode === 'approval' ? { enabled: true, checkboxOnly: true, mode: 'multiple' } : false"
    [kendoGridSelectBy]="'id'"
    [(selectedKeys)]="selectedIds"
    [sortable]="mode === 'approval' ? { allowUnsort: false } : { allowUnsort: true }"
    [sort]="sort"
    (sortChange)="onSortChange($event)"
  >
    <kendo-grid-messages [pagerOf]="t('pagerOf')" [pagerItems]="t('pagerItems')"
                         [noRecords]="t('noRecords')">
    </kendo-grid-messages>

    <ng-template kendoGridToolbarTemplate>
      <button *ngIf="mode === 'own'" kendoButton themeColor="primary" (click)="openCreate()">
        {{ t('create') }}
      </button>
      <button *ngIf="mode === 'approval'" kendoButton themeColor="success"
              [disabled]="!selectedIds.length" (click)="approveSelected()">
        {{ t('approveSelected') }}
      </button>
    </ng-template>

    <!-- The checkbox column stays on every screen size: bulk approval is the phone's main action. -->
    <kendo-grid-checkbox-column *ngIf="mode === 'approval'" [width]="40" [showSelectAll]="true"
                                class="expense-checkbox-col" headerClass="expense-checkbox-col">
    </kendo-grid-checkbox-column>

    <kendo-grid-column field="date" [title]="t('date')" [width]="120" media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        {{ dataItem.date | date:'dd.MM.yyyy' }}
      </ng-template>
    </kendo-grid-column>

    <!-- field is needed for the sortable header; the sort is mapped to Submitter.LastName/FirstName -->
    <kendo-grid-column *ngIf="mode === 'approval'" field="submitterLastName" [title]="t('submitter')"
                       media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        {{ dataItem.submitterFirstName }} {{ dataItem.submitterLastName }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column field="expenseTypeName" [title]="t('expenseType')"
                       media="(min-width: 450px)"></kendo-grid-column>

    <kendo-grid-column field="amount" [title]="t('amount')" [width]="120" [sortable]="false"
                       media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        CHF {{ dataItem.amount | number:'1.2-2' }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column field="status" [title]="t('status')" [width]="130"
                       media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        <span class="status-badge" [ngClass]="statusClass(dataItem.status)">
          {{ t('expenseStates.' + statusName(dataItem.status)) }}
        </span>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-command-column [title]="t('action')" [width]="320" media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        <ng-container *ngTemplateOutlet="rowActions; context: { $implicit: dataItem }"></ng-container>
      </ng-template>
    </kendo-grid-command-column>

    <!-- Mobile view: one card per expense instead of the columns above -->
    <kendo-grid-column media="(max-width: 450px)" [title]="t('entries')">
      <ng-template kendoGridCellTemplate let-dataItem>
        <h4 class="mbs">
          {{ dataItem.date | date:'dd.MM.yyyy' }} &middot; CHF {{ dataItem.amount | number:'1.2-2' }}
        </h4>

        <dl *ngIf="mode === 'approval'">
          <dt>{{ t('submitter') }}</dt>
          <dd>{{ dataItem.submitterFirstName }} {{ dataItem.submitterLastName }}</dd>
        </dl>
        <dl>
          <dt>{{ t('expenseType') }}</dt>
          <dd>{{ dataItem.expenseTypeName }}</dd>
        </dl>
        <dl>
          <dt>{{ t('status') }}</dt>
          <dd>
            <span class="status-badge" [ngClass]="statusClass(dataItem.status)">
              {{ t('expenseStates.' + statusName(dataItem.status)) }}
            </span>
          </dd>
        </dl>

        <ng-container *ngTemplateOutlet="rowActions; context: { $implicit: dataItem }"></ng-container>
      </ng-template>
    </kendo-grid-column>

    <ng-template kendoPagerTemplate>
      <div class="flex flexDirRow">
        <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
        <kendo-pager-numeric-buttons [buttonCount]="5"></kendo-pager-numeric-buttons>
        <kendo-pager-next-buttons></kendo-pager-next-buttons>
      </div>
      <div>
        <label for="ownPageBtnGroup" class="mrs">{{ t('resultsPerPage') }}</label>
        <kendo-buttongroup [selection]="'single'" id="ownPageBtnGroup">
          <button (click)="onPageSizeChange(pageSizes.Small)" kendoButton
                  [selected]="gridState.pageSize === pageSizes.Small" [toggleable]="true">
            {{ pageSizes.Small }}
          </button>
          <button (click)="onPageSizeChange(pageSizes.Medium)" kendoButton
                  [selected]="gridState.pageSize === pageSizes.Medium" [toggleable]="true">
            {{ pageSizes.Medium }}
          </button>
          <button (click)="onPageSizeChange(pageSizes.Large)" kendoButton
                  [selected]="gridState.pageSize === pageSizes.Large" [toggleable]="true">
            {{ pageSizes.Large }}
          </button>
        </kendo-buttongroup>
        <button kendoButton [title]="t('refresh')" (click)="onGridReload()" icon="reload"></button>
      </div>
    </ng-template>
  </kendo-grid>

  <app-delete-confirm-dialog
    [(opened)]="deletionDialogOpened"
    [message]="t('deletionMessage')"
    (confirmed)="deleteConfirmed()"
  ></app-delete-confirm-dialog>

  <app-expense-dialog
    *ngIf="dialogOpen"
    [expenseId]="dialogId"
    [editable]="dialogEditable"
    [allowDecide]="mode === 'approval'"
    (close)="closeDialog()">
  </app-expense-dialog>

  <app-expense-reason-dialog
    *ngIf="rejectOpen && rejectId"
    titleKey="reject"
    confirmKey="reject"
    (confirm)="confirmReject($event)"
    (cancel)="closeReject()">
  </app-expense-reason-dialog>
</ng-container>
